// ============================================================================
// UnmatchedFeatures ViewModel
// ============================================================================

import { useState, useCallback, useRef, useMemo } from "react";
import type { UnmatchedFeaturesProps, UnmatchedFeaturesViewModel } from "./UnmatchedFeatures.types";

/**
 * ViewModel hook for UnmatchedFeatures component
 */
export function useUnmatchedFeaturesViewModel(props: UnmatchedFeaturesProps): UnmatchedFeaturesViewModel {
	const { issues, currentIssueIndex, skippedIssueIds, onDeleteJob, onAssignToJob, onSplitFeatures, onUploadBoundary } =
		props;

	// Visibility state
	const [hideUnmatchedFeatures, setHideUnmatchedFeatures] = useState(false);
	const [hiddenNearbyJobIds, setHiddenNearbyJobIds] = useState<Set<string>>(new Set());

	// Lasso selection state
	const [lassoMode, setLassoMode] = useState(false);
	const [selectedFeatureIds, setSelectedFeatureIds] = useState<Set<string>>(new Set());

	// Upload state
	const [isUploading, setIsUploading] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);

	// Derived state
	const hasIssues = issues.length > 0;
	const currentIssue = hasIssues ? (issues[currentIssueIndex] ?? null) : null;
	const isSkipped = currentIssue ? skippedIssueIds.has(currentIssue.id) : false;

	// Unmatched feature count
	const unmatchedFeatureCount = currentIssue?.unmatchedFeatures?.features?.length || 0;

	// Source file name (filename only, not full path)
	const sourceFileName = useMemo(() => {
		if (!currentIssue?.filePath) return "";
		const parts = currentIssue.filePath.split(/[/\\]/);
		return parts[parts.length - 1] || currentIssue.filePath;
	}, [currentIssue]);

	// Can split if features are selected and onSplitFeatures is provided
	const canSplit = selectedFeatureIds.size > 0 && !!onSplitFeatures;

	// Toggle unmatched features visibility
	const toggleUnmatchedFeaturesVisibility = useCallback(() => {
		setHideUnmatchedFeatures((prev) => !prev);
	}, []);

	// Toggle nearby job visibility
	const toggleNearbyJobVisibility = useCallback((jobId: string) => {
		setHiddenNearbyJobIds((prev) => {
			const next = new Set(prev);
			if (next.has(jobId)) {
				next.delete(jobId);
			} else {
				next.add(jobId);
			}
			return next;
		});
	}, []);

	// Show/hide all nearby jobs
	const showAllNearbyJobs = useCallback(() => {
		setHiddenNearbyJobIds(new Set());
	}, []);

	const hideAllNearbyJobs = useCallback(() => {
		if (currentIssue) {
			setHiddenNearbyJobIds(new Set(currentIssue.nearbyJobs.map((n) => n.job.id)));
		}
	}, [currentIssue]);

	// Handle delete job
	const handleDeleteJob = useCallback(() => {
		if (currentIssue) {
			onDeleteJob(currentIssue.job.id);
		}
	}, [currentIssue, onDeleteJob]);

	// Handle assign to job
	const handleAssignToJob = useCallback(
		(targetJobId: string) => {
			if (currentIssue) {
				onAssignToJob(currentIssue.job.id, targetJobId);
			}
		},
		[currentIssue, onAssignToJob],
	);

	// Toggle lasso mode or clear selection
	const handleToggleLasso = useCallback(() => {
		if (selectedFeatureIds.size > 0) {
			// Clear selection
			setSelectedFeatureIds(new Set());
		} else {
			// Toggle lasso mode
			setLassoMode((prev) => !prev);
		}
	}, [selectedFeatureIds.size]);

	// Handle lasso completion
	const handleLassoComplete = useCallback((selectedIds: Set<string>) => {
		setSelectedFeatureIds(selectedIds);
		setLassoMode(false);
	}, []);

	// Handle split selected features
	const handleSplitFeatures = useCallback(() => {
		if (currentIssue && selectedFeatureIds.size > 0 && onSplitFeatures) {
			onSplitFeatures(currentIssue.job.id, selectedFeatureIds);
			// Clear selection after split
			setSelectedFeatureIds(new Set());
		}
	}, [currentIssue, selectedFeatureIds, onSplitFeatures]);

	// Upload is available when callback is provided
	const canUpload = !!onUploadBoundary;

	// Trigger file input click
	const handleUploadClick = useCallback(() => {
		fileInputRef.current?.click();
	}, []);

	// Handle file selection
	const handleFileChange = useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			const files = event.target.files;
			if (!files || files.length === 0 || !currentIssue || !onUploadBoundary) return;

			setIsUploading(true);
			try {
				await onUploadBoundary(currentIssue.job.id, Array.from(files));
			} finally {
				setIsUploading(false);
				// Reset file input so the same file can be selected again
				if (fileInputRef.current) {
					fileInputRef.current.value = "";
				}
			}
		},
		[currentIssue, onUploadBoundary],
	);

	return {
		hasIssues,
		currentIssue,
		isSkipped,
		unmatchedFeatureCount,
		sourceFileName,
		hideUnmatchedFeatures,
		hiddenNearbyJobIds,
		toggleUnmatchedFeaturesVisibility,
		toggleNearbyJobVisibility,
		showAllNearbyJobs,
		hideAllNearbyJobs,
		handleDeleteJob,
		handleAssignToJob,
		lassoMode,
		selectedFeatureIds,
		handleToggleLasso,
		handleLassoComplete,
		handleSplitFeatures,
		canSplit,
		isUploading,
		fileInputRef,
		handleUploadClick,
		handleFileChange,
		canUpload,
	};
}
